import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { UserContext } from '../context/UserContext';
import API from '../config/axiosConfig';
import Pagination from './Pagination';
import '../pages/Settings.scss';
import './AuditSettings.scss';

// Super-admin only. Reads GET /api/audit — see docs/Audit.md for the record
// shape, the action catalog, and how to add a new audited action.

type AuditActor = {
  userId?: string | null;
  email?: string | null;
  name?: string | null;
  isSuperAdmin?: boolean;
};

type AuditTarget = {
  type?: string | null;
  id?: string | null;
  name?: string | null;
};

type AuditRequestInfo = {
  method?: string | null;
  path?: string | null;
  ip?: string | null;
  userAgent?: string | null;
  statusCode?: number | null;
};

type AuditRow = {
  _id: string;
  action: string;
  category: string;
  severity: string;
  outcome: string;
  description?: string | null;
  actor?: AuditActor;
  target?: AuditTarget;
  hospital?: string | null;
  hospitalName?: string | null;
  department?: string | null;
  departmentName?: string | null;
  metadata?: Record<string, unknown>;
  request?: AuditRequestInfo;
  env?: string | null;
  createdAt: string;
};

type AuditPage = {
  items: AuditRow[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
};

type Option = { key: string; label: string };

type AuditMeta = {
  categories: Option[];
  severities: Option[];
  outcomes: Option[];
  actions: { action: string; category: string; label: string }[];
  retentionDays?: number;
};

type AuditScopes = {
  hospitals: Option[];
  departments: Option[];
  users: Option[];
};

const EMPTY_SCOPES: AuditScopes = { hospitals: [], departments: [], users: [] };

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 400;
const DEFAULT_ERROR_MESSAGE = 'שגיאה בטעינת יומן הביקורת. אנא נסה שנית.';

const formatDateTime = (value: string): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.toLocaleDateString('en-GB')} ${date.toLocaleTimeString('en-GB')}`;
};

const hasMetadata = (metadata?: Record<string, unknown>): boolean =>
  Boolean(metadata && Object.keys(metadata).length > 0);

const AuditSettings: React.FC = () => {
  const { user } = useContext(UserContext);
  const isSuperAdmin = Boolean(user && user.isSuperAdmin);

  const [meta, setMeta] = useState<AuditMeta | null>(null);
  const [scopes, setScopes] = useState<AuditScopes>(EMPTY_SCOPES);
  const [data, setData] = useState<AuditPage | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const [page, setPage] = useState<number>(1);
  const [category, setCategory] = useState<string>('');
  const [action, setAction] = useState<string>('');
  const [outcome, setOutcome] = useState<string>('');
  const [hospital, setHospital] = useState<string>('');
  const [department, setDepartment] = useState<string>('');
  const [auditUser, setAuditUser] = useState<string>('');
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [search, setSearch] = useState<string>('');

  // Debounce the free-text box so typing doesn't fire a query per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Any filter change restarts at page 1 — otherwise a narrower filter can land
  // the user on a page that no longer exists.
  useEffect(() => {
    setPage(1);
  }, [category, action, outcome, hospital, department, auditUser, from, to, search]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    let cancelled = false;

    // Filter dropdowns are a convenience — the table still works without them,
    // so both of these fail quietly.
    API.get<AuditMeta>('/api/audit/meta', { handlingErrorLocally: true })
      .then((res) => {
        if (!cancelled) setMeta(res.data);
      })
      .catch(() => {
        if (!cancelled) setMeta(null);
      });

    API.get<AuditScopes>('/api/audit/scopes', { handlingErrorLocally: true })
      .then((res) => {
        if (!cancelled) setScopes(res.data || EMPTY_SCOPES);
      })
      .catch(() => {
        if (!cancelled) setScopes(EMPTY_SCOPES);
      });

    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin]);

  // Actions offered in the dropdown follow the selected category, so the list
  // stays short even as the catalog grows.
  const actionOptions = useMemo(() => {
    if (!meta) return [];
    return meta.actions.filter((entry) => !category || entry.category === category);
  }, [meta, category]);

  useEffect(() => {
    if (action && !actionOptions.some((entry) => entry.action === action)) {
      setAction('');
    }
  }, [action, actionOptions]);

  const loadPage = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const params: Record<string, string | number> = { page, limit: PAGE_SIZE };
      if (category) params.category = category;
      if (action) params.action = action;
      if (outcome) params.outcome = outcome;
      if (hospital) params.hospital = hospital;
      if (department) params.department = department;
      if (auditUser) params.user = auditUser;
      if (search) params.q = search;
      // The date inputs are day-granular; widen them to the full day so "to"
      // includes everything that happened on the chosen date.
      if (from) params.from = new Date(`${from}T00:00:00`).toISOString();
      if (to) params.to = new Date(`${to}T23:59:59.999`).toISOString();

      const response = await API.get<AuditPage>('/api/audit', {
        params,
        handlingErrorLocally: true,
      });
      setData(response.data);
      setExpandedId(null);
    } catch (err: unknown) {
      console.error('Loading audit log failed:', err);
      const apiError = err as { response?: { data?: { error?: { hebMessage?: string; message?: string } } } };
      setError(
        apiError.response?.data?.error?.hebMessage
        || apiError.response?.data?.error?.message
        || DEFAULT_ERROR_MESSAGE
      );
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [page, category, action, outcome, hospital, department, auditUser, search, from, to]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    loadPage();
  }, [isSuperAdmin, loadPage]);

  if (!isSuperAdmin) {
    return null;
  }

  const clearFilters = () => {
    setCategory('');
    setAction('');
    setOutcome('');
    setHospital('');
    setDepartment('');
    setAuditUser('');
    setFrom('');
    setTo('');
    setSearchInput('');
  };

  const hasFilters = Boolean(
    category || action || outcome || hospital || department || auditUser || from || to || searchInput
  );
  const rows = data ? data.items : [];

  return (
    <div className="settings-section audit-settings">
      <h2 className="section-title">יומן ביקורת</h2>
      <p className="audit-description">
        תיעוד של פעולות רגישות במערכת: התחברויות, העלאת והורדת קבצים, מחיקות, שינויי הרשאות
        ופעולות ניהול. הרשומות נשמרות בשרת ואינן ניתנות לעריכה מהממשק
        {meta?.retentionDays ? ` (נשמרות ${meta.retentionDays} ימים)` : ''}.
      </p>

      <div className="audit-filters">
        <input
          type="text"
          className="input-field"
          placeholder="חיפוש לפי משתמש, יעד או פעולה"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />

        <select
          className="select-field"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          aria-label="קטגוריה"
        >
          <option value="">כל הקטגוריות</option>
          {(meta ? meta.categories : []).map((option) => (
            <option key={option.key} value={option.key}>{option.label}</option>
          ))}
        </select>

        <select
          className="select-field"
          value={action}
          onChange={(e) => setAction(e.target.value)}
          aria-label="פעולה"
        >
          <option value="">כל הפעולות</option>
          {actionOptions.map((option) => (
            <option key={option.action} value={option.action}>{option.label}</option>
          ))}
        </select>

        <select
          className="select-field"
          value={outcome}
          onChange={(e) => setOutcome(e.target.value)}
          aria-label="תוצאה"
        >
          <option value="">הכל</option>
          {(meta ? meta.outcomes : []).map((option) => (
            <option key={option.key} value={option.key}>{option.label}</option>
          ))}
        </select>

        <select
          className="select-field"
          value={hospital}
          onChange={(e) => setHospital(e.target.value)}
          aria-label="בית חולים"
        >
          <option value="">כל בתי החולים</option>
          {scopes.hospitals.map((option) => (
            <option key={option.key} value={option.key}>{option.label}</option>
          ))}
        </select>

        <select
          className="select-field"
          value={department}
          onChange={(e) => setDepartment(e.target.value)}
          aria-label="מחלקה"
        >
          <option value="">כל המחלקות</option>
          {scopes.departments.map((option) => (
            <option key={option.key} value={option.key}>{option.label}</option>
          ))}
        </select>

        {/* Free text so an email that predates the current user list still works,
            with the recorded users offered as suggestions. */}
        <input
          type="text"
          className="input-field"
          list="audit-user-options"
          placeholder="משתמש (אימייל או שם)"
          value={auditUser}
          onChange={(e) => setAuditUser(e.target.value)}
          aria-label="משתמש"
        />
        <datalist id="audit-user-options">
          {scopes.users.map((option) => (
            <option key={option.key} value={option.key}>{option.label}</option>
          ))}
        </datalist>

        <label className="audit-date-filter">
          מתאריך
          <input type="date" className="input-field" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>

        <label className="audit-date-filter">
          עד תאריך
          <input type="date" className="input-field" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>

        <button type="button" className="button-secondary" onClick={clearFilters} disabled={!hasFilters}>
          נקה סינון
        </button>
        <button type="button" className="button-primary" onClick={loadPage} disabled={isLoading}>
          רענן
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {isLoading && <div className="loading">טוען...</div>}

      {!isLoading && !error && rows.length === 0 && (
        <div className="no-data">לא נמצאו רשומות התואמות לסינון</div>
      )}

      {!isLoading && rows.length > 0 && (
        <>
          <div className="audit-summary">
            נמצאו {data?.total} רשומות · מוצג עמוד {data?.page} מתוך {data?.totalPages}
          </div>

          <div className="audit-table-wrapper">
            <table className="hospitals-table audit-table">
              <thead>
                <tr>
                  <th>תאריך ושעה</th>
                  <th>פעולה</th>
                  <th>קטגוריה</th>
                  <th>משתמש</th>
                  <th>יעד</th>
                  <th>בית חולים</th>
                  <th>מחלקה</th>
                  <th>תוצאה</th>
                  <th>פרטים</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const isExpanded = expandedId === row._id;
                  return (
                    <React.Fragment key={row._id}>
                      <tr className={`severity-${row.severity} outcome-${row.outcome}`}>
                        <td className="audit-cell-date">{formatDateTime(row.createdAt)}</td>
                        <td>
                          <div className="audit-action-label">{row.description || row.action}</div>
                          <div className="audit-action-key">{row.action}</div>
                        </td>
                        <td>{meta?.categories.find((c) => c.key === row.category)?.label || row.category}</td>
                        <td>
                          <div>{row.actor?.name || '—'}</div>
                          <div className="audit-actor-email">{row.actor?.email || 'לא מזוהה'}</div>
                        </td>
                        <td className="audit-cell-target">
                          {row.target?.name || row.target?.id || '—'}
                        </td>
                        <td>{row.hospitalName || row.hospital || '—'}</td>
                        <td>{row.departmentName || row.department || '—'}</td>
                        <td>
                          <span className={row.outcome === 'failure' ? 'audit-failure' : 'audit-success'}>
                            {row.outcome === 'failure' ? 'נכשל' : 'הצליח'}
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="button-secondary audit-details-button"
                            onClick={() => setExpandedId(isExpanded ? null : row._id)}
                          >
                            {isExpanded ? 'סגור' : 'הצג'}
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="audit-details-row">
                          <td colSpan={9}>
                            <div className="audit-details">
                              <div><strong>נתיב:</strong> {row.request?.method} {row.request?.path || '—'}</div>
                              <div><strong>קוד תשובה:</strong> {row.request?.statusCode ?? '—'}</div>
                              <div><strong>כתובת IP:</strong> {row.request?.ip || '—'}</div>
                              <div><strong>סוג יעד:</strong> {row.target?.type || '—'}</div>
                              <div><strong>מזהה יעד:</strong> {row.target?.id || '—'}</div>
                              <div><strong>סביבה:</strong> {row.env || '—'}</div>
                              <div className="audit-user-agent"><strong>דפדפן:</strong> {row.request?.userAgent || '—'}</div>
                              {hasMetadata(row.metadata) && (
                                <pre className="audit-metadata">{JSON.stringify(row.metadata, null, 2)}</pre>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={data?.page || 1}
            totalPages={data?.totalPages || 1}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
};

export default AuditSettings;
